// Libraries
import React, { Component, createRef } from 'react';

// Components
import { translate } from '../../globals/translations';
import {
    formatAddress,
    getIntersectionCoordinates,
    roadLookup,
} from '../../abstract/maps';

/**
 * RoadInput
 *
 * Re-usable component that renders and controls the user road or intersection lookup
 * using the Google Maps API
 *
 * @since 0.5
 *
 * @param props
 * @return {object} The form input fields for getting a road or insterstion
 *
 */
class RoadInput extends Component {
    constructor(props) {
        super(props);

        this.state = {
            roadNameInput: props.roadValue || '',
            nearestCrossroadInput: props.crossroadValue || '',
            crossroadDisabled: !props.roadValue,
            roadObject: props.roadObject || { data: { locality: '' } },
            nearestCrossroadObject: '',
            roadApi: null,
            crossroadApi: null,
            highlightedIndex: -1,
        };

        this.handleChange = this.handleChange.bind(this);
        this.intersectionLookupBound = this.intersectionLookupBound.bind(this);
        this.handlePlaceChanged = this.handlePlaceChanged.bind(this);

        this.optionRefs = [];
        this.roadInputRef = createRef();
        this.crossroadInputRef = createRef();
    }

    componentDidMount() {
        const roadApi = roadLookup(this.roadInputRef.current);
        const crossroadApi = roadLookup(this.crossroadInputRef.current);

        this.setState({ roadApi, crossroadApi }, () => {
            roadApi.addListener('place_changed', this.handlePlaceChanged);
        });
    }

    /**
     * handlePlaceChanged
     *
     * Event handler method handlePlaceChanged() is called when a place is selected from
     * the Google Maps Places Autocomplete dropdown. It retrieves the Autocomplete instance
     * from the roadApi state, gets the selected place object, formats the address using
     * the formatAddress() utility function, updates the component state with the formatted address,
     * the selected place object, and resets other relevant state values.
     * Additionally, it signals to the parent component about the updated road information
     * by invoking the onRoadUpdate() callback with the formatted place object.
     */
    handlePlaceChanged() {
        const roadObject = this.state.roadApi.getPlace();
        const formattedObject = formatAddress(roadObject);

        this.setState({
            roadNameInput: formattedObject.address,
            roadObject: formattedObject,
            nearestCrossroadInput: '',
            crossroadDisabled: false,
        });

        this.props.onRoadUpdate(formattedObject);
    }

    /**
     * handleChange
     *
     * Callback method for the onchange() event of the input field to properly set input value
     * and component state
     */
    handleChange(event) {
        const { name, value } = event.target;

        this.setState({ [name]: value });

        if (name === 'roadNameInput') {
            const crossroadDisabled = value === '';

            if (this.props.onCrossroadDisabledUpdate) {
                this.props.onCrossroadDisabledUpdate(crossroadDisabled);
            }

            this.setState({
                crossroadDisabled,
                nearestCrossroadInput: crossroadDisabled
                    ? ''
                    : this.state.nearestCrossroadInput,
                nearestCrossroadObject: crossroadDisabled
                    ? ''
                    : this.state.nearestCrossroadObject,
            });
        }

        // Additional logic for nearestCrossroadInput
        if (
            name === 'nearestCrossroadInput' &&
            value !== '' &&
            this.props.isApiLoaded
        ) {
            this.intersectionLookupBound(this.state.roadObject.data.locality);
        }
    }

    intersectionLookupBound(city) {
        const strictBounds = !!city;

        function crossRoadAutocomplete(bounds, roadInputObject) {
            const crossroad = roadInputObject.state.crossroadApi;

            crossroad.setOptions({
                bounds: bounds ? bounds : null,
                strictBounds,
                types: ['geocode'],
                componentRestrictions: { country: 'CA' },
            });

            crossroad.addListener('place_changed', () => {
                const formattedObject = formatAddress(crossroad.getPlace());

                roadInputObject.setState({
                    nearestCrossroadInput: formattedObject.address,
                    nearestCrossroadObject: formattedObject,
                });

                getIntersectionCoordinates(
                    roadInputObject,
                    formattedObject,
                    ''
                );
            });
        }

        function getLocations(crossRoadAutocomplete, roadInputObject) {
            if (roadInputObject.state.roadObject.address != undefined) {
                let geocoder = new google.maps.Geocoder();

                geocoder.geocode(
                    {
                        address:
                            roadInputObject.state.roadObject.address + ', CA',
                    },
                    function (results, status) {
                        if (results && results.length > 0) {
                            let defaultBounds = 0;

                            defaultBounds = new google.maps.LatLngBounds(
                                new google.maps.LatLng(
                                    results[0].geometry.viewport
                                        .getSouthWest()
                                        .lat(),
                                    results[0].geometry.viewport
                                        .getSouthWest()
                                        .lng()
                                ),
                                new google.maps.LatLng(
                                    results[0].geometry.viewport
                                        .getNorthEast()
                                        .lat(),
                                    results[0].geometry.viewport
                                        .getNorthEast()
                                        .lng()
                                )
                            );

                            if (
                                status == google.maps.GeocoderStatus.OK &&
                                defaultBounds
                            ) {
                                crossRoadAutocomplete(
                                    defaultBounds,
                                    roadInputObject
                                );
                            }
                        }
                    }
                );
            } else {
                crossRoadAutocomplete(false, roadInputObject);
            }
        }

        // Get city bounds of the selected road
        getLocations(crossRoadAutocomplete, this);
    }

    /**
     * clearInput
     * Clears the text input from the road name input field
     */
    clearInput = () => {
        this.setState({
            roadNameInput: '',
        });
    };

    /**
     * render
     *
     * Return the input fields for road lookup
     */
    render() {
        const roadField = {
            name: 'roadNameInput',
            id: 'roadName',
            translation_key: 'road',
            label: translate('road_label') + ' *',
            placeholder: translate('road_label'),
        };

        const crossroadField = {
            name: 'nearestCrossroadInput',
            id: 'nearestCrossroad',
            translation_key: 'crossroad',
            label: `${translate('crossroad_label')} ${translate('optional')}`,
            placeholder: translate('crossroad_label'),
        };

        const selectedRoadContent =
            this.state.roadNameInput && !this.props.crossroadDisabled ? (
                <div className="road-confirm">
                    <p>{translate('you_have_selected')}:</p>
                    <p>
                        {this.state.roadNameInput.split(/,(.+)/)[0]}
                        <br />
                        {this.state.nearestCrossroadInput && (
                            <>
                                {translate('at_intersection')}{' '}
                                {
                                    this.state.nearestCrossroadInput.split(
                                        /,(.+)/
                                    )[0]
                                }
                                <br />
                            </>
                        )}
                        {this.state.roadNameInput.split(/,(.+)/)[1]}
                    </p>
                </div>
            ) : null;

        return (
            <div className="road-input-container">
                <div
                    className="form-group"
                    data-translation-key={roadField.translation_key}
                >
                    <label htmlFor={roadField.id}>{roadField.label}</label>
                    <div className="road-input-wrapper">
                        <input
                            type="text"
                            className="form-control required"
                            id={roadField.id}
                            placeholder={`${roadField.placeholder}`}
                            name={roadField.name}
                            value={this.state.roadNameInput}
                            onChange={this.handleChange}
                            autoComplete="off"
                            ref={this.roadInputRef}
                            aria-required="true"
                            aria-describedby={`${roadField.name}-description`}
                        />
                        {this.state.roadNameInput && (
                            <button
                                type="button"
                                className="clear-button"
                                onClick={this.clearInput}
                                aria-label={translate('clear_input')}
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    tabindex="-1"
                                    focusable="false"
                                    aria-hidden="true"
                                >
                                    <rect
                                        width="20"
                                        height="20"
                                        rx="10"
                                        fill="white"
                                    />
                                    <path
                                        d="M13.5156 7.53906L11.0547 10L13.5156 12.4844C13.8203 12.7656 13.8203 13.2578 13.5156 13.5391C13.2344 13.8438 12.7422 13.8438 12.4609 13.5391L10 11.0781L7.51562 13.5391C7.23438 13.8438 6.74219 13.8438 6.46094 13.5391C6.15625 13.2578 6.15625 12.7656 6.46094 12.4844L8.92188 10L6.46094 7.53906C6.15625 7.25781 6.15625 6.76562 6.46094 6.48438C6.74219 6.17969 7.23438 6.17969 7.51562 6.48438L10 8.94531L12.4609 6.48438C12.7422 6.17969 13.2344 6.17969 13.5156 6.48438C13.8203 6.76562 13.8203 7.25781 13.5156 7.53906Z"
                                        fill="#08589E"
                                    />
                                </svg>
                            </button>
                        )}
                    </div>
                    <span
                        aria-describedby={`${roadField.name}-description`}
                        className="form-error-msg -hidden"
                        id={roadField.name}
                        data-for={roadField.name}
                    >
                        {translate('please_select_road')}
                    </span>
                </div>

                <div
                    className="form-group"
                    data-translation-key={crossroadField.translation_key}
                >
                    <label htmlFor={crossroadField.id}>
                        {crossroadField.label}
                    </label>
                    <input
                        type="text"
                        className="form-control"
                        id={crossroadField.id}
                        placeholder={crossroadField.placeholder}
                        name={crossroadField.name}
                        value={this.state.nearestCrossroadInput}
                        onChange={this.handleChange}
                        disabled={this.props.crossroadDisabled}
                        autoComplete="off"
                        ref={this.crossroadInputRef}
                        aria-describedby={`${crossroadField.id}-description`}
                    />
                    <span
                        aria-describedby={`${crossroadField.id}-description`}
                        className="form-helper-text"
                    >
                        {translate('choose_landmark')}
                    </span>
                </div>
                {selectedRoadContent}
            </div>
        );
    }
}

export default RoadInput;
